import clsx from 'clsx';
import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import FeedBookmark from '@components/Bookmark/FeedBookmark';
import CopyLinkButton from '@components/pages/Organization/AccountSection/RegistrationLinks/CopyLinkButton';
import { Theme } from '@interfaces/global';
import { MIN_COMMENTS_COUNT } from '@interfaces/homepage';
import { ContentType } from '@tracking';
import { articlePath } from '@utils/queryStringHelper';

import { Author } from '../../interfaces/authors';
import CommentsCount from '../FeedItems/CommentsCount';
import NameList from '../NameList';
import TimeAgo from '../TimeAgo';

interface BylineProps {
  authors?: Author[];
  authorsClassName?: string;
  bookmarkContentId?: number;
  bookmarkContentType?: ContentType;
  className?: string;
  commentsCount?: number;
  isRelatedArticle?: boolean;
  linkToCopy?: string;
  publishedAt?: string;
  newsletterSlug?: string;
  slug?: string;
  timeFromNow?: boolean;
  withTooltip?: boolean;
  theme?: Theme;
}

const queryClient = new QueryClient();

const Byline = ({
  authors,
  authorsClassName,
  bookmarkContentId,
  bookmarkContentType,
  className,
  commentsCount = 0,
  isRelatedArticle = false,
  linkToCopy,
  publishedAt,
  newsletterSlug,
  slug,
  timeFromNow,
  withTooltip = false,
  theme = Theme.Light,
}: BylineProps) => {
  const hasBookmark = bookmarkContentId != null && bookmarkContentType != null;

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className={clsx('font-suisse-screen', className, {
          'text-byline-sm': !isRelatedArticle,
          'text-disclosure-sm': isRelatedArticle,
          'flex flex-wrap items-center': commentsCount >= MIN_COMMENTS_COUNT || hasBookmark,
          'flex justify-between': linkToCopy,
          'text-primary-black': theme === Theme.Light,
          'text-white': theme === Theme.Dark,
        })}
      >
        {authors?.length > 0 && !linkToCopy && (
          <div className="mr-10 inline-block">
            <NameList
              authors={authors}
              authorClassName={authorsClassName}
              theme={theme}
              withTooltip={withTooltip}
            />
          </div>
        )}
        {publishedAt && (
          <TimeAgo
            className={clsx('inline-block', {
              'text-grayscale-gray dark:text-grayscale-charcoal': theme === Theme.Light,
              'text-grayscale-beige': theme === Theme.Dark,
              'mr-10': commentsCount >= MIN_COMMENTS_COUNT,
            })}
            date={publishedAt}
            timeFromNow={timeFromNow}
          />
        )}
        {linkToCopy && (
          <CopyLinkButton
            className="text-grayscale-gray"
            label="Copy link"
            linkToCopy={linkToCopy}
          />
        )}
        {hasBookmark && (
          <FeedBookmark
            className="mx-10"
            contentId={bookmarkContentId}
            contentType={bookmarkContentType}
          />
        )}
        {commentsCount >= MIN_COMMENTS_COUNT && !linkToCopy && (
          <CommentsCount
            numberOfComment={commentsCount}
            url={articlePath({ slug, newsletterSlug })}
          />
        )}
      </div>
    </QueryClientProvider>
  );
};

export default Byline;
